import React, { useCallback, useState } from 'react';
import { Modal, Carousel } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import './MediaGrid.css';

// Custom Arrow Components
const CustomArrow = ({ direction, onClick, visible = true }: { direction: 'prev' | 'next'; onClick?: () => void; visible?: boolean }) => {
  if (!visible) return null;

  return (
    <button
      className={`custom-carousel-${direction}`}
      onClick={onClick}
      style={{
        position: 'absolute',
        [direction === 'prev' ? 'left' : 'right']: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        borderColor: 'transparent',
        color: 'white',
        borderRadius: '50%',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        transition: 'all 0.3s ease',
        border: 'none',
        cursor: 'pointer'
      }}
    >
      {direction === 'prev' ? <LeftOutlined /> : <RightOutlined />}
    </button>
  );
};

export interface MediaItem {
  type: 'video' | 'image';
  videoId?: string;
  image?: { image_url: string };
  id: string;
}

interface MediaGridProps {
  mediaItems: MediaItem[];
  onImageClick?: (index: number) => void;
  className?: string;
}

const MediaGrid: React.FC<MediaGridProps> = ({
  mediaItems,
  onImageClick,
  className = ''
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const handleImageClick = useCallback((index: number) => {
    setSelectedImageIndex(index);
    setIsModalVisible(true);
    if (onImageClick) {
      onImageClick(index);
    }
  }, [onImageClick]);

  const handleMoreClick = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const hasVideo = mediaItems.some(item => item.type === 'video');

  if (mediaItems.length === 0) {
    return null;
  }

  const renderSingleMedia = () => (
    <div className="single-media">
      {mediaItems[0].type === 'video' ? (
        <div className="video-wrapper">
          <iframe
            src={`https://www.youtube.com/embed/${mediaItems[0].videoId}`}
            title="YouTube video"
            frameBorder="0"
            allowFullScreen
            className="media-video"
          />
        </div>
      ) : (
        <div className="image-wrapper">
          <img
            src={mediaItems[0].image?.image_url}
            alt=""
            className="media-image"
            onClick={() => handleImageClick(0)}
          />
        </div>
      )}
    </div>
  );

  const renderTwoMedia = () => (
    <div className="media-grid-2">
      {mediaItems.map((mediaItem, index) => (
        <div key={mediaItem.id} className="media-item-2">
          {mediaItem.type === 'video' ? (
            <div className="video-wrapper-grid">
              <iframe
                src={`https://www.youtube.com/embed/${mediaItem.videoId}`}
                title="YouTube video"
                frameBorder="0"
                allowFullScreen
                className="media-video"
              />
            </div>
          ) : (
            <div className="image-wrapper-grid">
              <img
                src={mediaItem.image?.image_url}
                alt=""
                className="media-image-grid"
                onClick={() => handleImageClick(hasVideo ? index - 1 : index)}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderThreeMedia = () => (
    <div className="media-grid-3">
      <div className="media-column-1">
        {mediaItems[0].type === 'video' ? (
          <div className="video-wrapper-grid large">
            <iframe
              src={`https://www.youtube.com/embed/${mediaItems[0].videoId}`}
              title="YouTube video"
              frameBorder="0"
              allowFullScreen
              className="media-video"
            />
          </div>
        ) : (
          <div className="image-wrapper-grid large">
            <img
              src={mediaItems[0].image?.image_url}
              alt=""
              className="media-image-grid"
              onClick={() => handleImageClick(hasVideo ? -1 : 0)}
            />
          </div>
        )}
      </div>
      <div className="media-column-2">
        {mediaItems.slice(1).map((mediaItem, index) => (
          <div key={mediaItem.id} className="media-item-3">
            {mediaItem.type === 'video' ? (
              <div className="video-wrapper-grid small">
                <iframe
                  src={`https://www.youtube.com/embed/${mediaItem.videoId}`}
                  title="YouTube video"
                  frameBorder="0"
                  allowFullScreen
                  className="media-video"
                />
              </div>
            ) : (
              <div className="image-wrapper-grid small">
                <img
                  src={mediaItem.image?.image_url}
                  alt=""
                  className="media-image-grid"
                  onClick={() => handleImageClick(hasVideo ? index : index + 1)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderFourOrMoreMedia = () => (
    <div className="media-grid-4">
      {mediaItems.slice(0, 3).map((mediaItem, index) => (
        <div key={mediaItem.id} className="media-item-4">
          {mediaItem.type === 'video' ? (
            <div className="video-wrapper-grid">
              <iframe
                src={`https://www.youtube.com/embed/${mediaItem.videoId}`}
                title="YouTube video"
                frameBorder="0"
                allowFullScreen
                className="media-video"
              />
            </div>
          ) : (
            <div className="image-wrapper-grid">
              <img
                src={mediaItem.image?.image_url}
                alt=""
                className="media-image-grid"
                onClick={() => handleImageClick(hasVideo ? index - 1 : index)}
              />
            </div>
          )}
        </div>
      ))}
      <div className="media-item-4 media-more" onClick={handleMoreClick}>
        {mediaItems[3].type === 'video' ? (
          <div className="video-wrapper-grid">
            <iframe
              src={`https://www.youtube.com/embed/${mediaItems[3].videoId}`}
              title="YouTube video"
              frameBorder="0"
              allowFullScreen
              className="media-video"
            />
            <div className="media-overlay">
              <span className="more-count">+{mediaItems.length - 4}</span>
            </div>
          </div>
        ) : (
          <div className="image-wrapper-grid">
            <img
              src={mediaItems[3].image?.image_url}
              alt=""
              className="media-image-grid"
            />
            <div className="media-overlay">
              <span className="more-count">+{mediaItems.length - 4}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderFourMedia = () => (
    <div className="media-grid-4">
      {mediaItems.map((mediaItem, index) => (
        <div key={mediaItem.id} className="media-item-4">
          {mediaItem.type === 'video' ? (
            <div className="video-wrapper-grid">
              <iframe
                src={`https://www.youtube.com/embed/${mediaItem.videoId}`}
                title="YouTube video"
                frameBorder="0"
                allowFullScreen
                className="media-video"
              />
            </div>
          ) : (
            <div className="image-wrapper-grid">
              <img
                src={mediaItem.image?.image_url}
                alt=""
                className="media-image-grid"
                onClick={() => handleImageClick(hasVideo ? index - 1 : index)}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderGrid = () => {
    if (mediaItems.length === 1) return renderSingleMedia();
    if (mediaItems.length === 2) return renderTwoMedia();
    if (mediaItems.length === 3) return renderThreeMedia();
    if (mediaItems.length === 4) return renderFourMedia();
    return renderFourOrMoreMedia();
  };

  return (
    <>
      <div className={`media-container ${className}`}>
        {renderGrid()}
      </div>

      {/* Media Modal */}
      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        centered
        width="90vw"
        className="image-modal"
      >
        <Carousel
          initialSlide={selectedImageIndex}
          arrows
          prevArrow={<CustomArrow direction="prev" />}
          nextArrow={<CustomArrow direction="next" />}
        >
          {mediaItems.map((mediaItem) => (
            <div key={mediaItem.id} className="modal-image-container">
              {mediaItem.type === 'video' ? (
                <div className="modal-video-wrapper">
                  <iframe
                    src={`https://www.youtube.com/embed/${mediaItem.videoId}`}
                    title="YouTube video"
                    frameBorder="0"
                    allowFullScreen
                    className="modal-video"
                  />
                </div>
              ) : (
                <img
                  src={mediaItem.image?.image_url}
                  alt=""
                  className="modal-image"
                />
              )}
            </div>
          ))}
        </Carousel>
      </Modal>
    </>
  );
};

export default MediaGrid;